import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import faculty, { SCHOOL_INFO } from '../data/faculty';

const MailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75a2.25 2.25 0 012.25-2.25h2.25A.75.75 0 017.5 5.25v2.25a.75.75 0 01-.22.53l-1.5 1.5a16.5 16.5 0 008.47 8.47l1.5-1.5a.75.75 0 01.53-.22h2.25a.75.75 0 01.75.75v2.25a2.25 2.25 0 01-2.25 2.25A15.75 15.75 0 012.25 6.75z" />
  </svg>
);

const LocationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const CATEGORY_LABEL = {
  administration: 'Administrator',
  office:         'Office Staff',
  related:        'Related Services',
  enl:            'ENL Teacher',
  counselors:     'School Counselor',
  elementary:     'Elementary Teacher',
  middle:         'Middle School Teacher',
  cluster:        'Cluster Teacher',
};

const getInitials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const FacultyProfilePage = () => {
  const { slug } = useParams();
  const person = faculty.find((p) => p.slug === slug || p.id === slug);

  if (!person) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main id="main-content" className="pt-0">
          <section
            className="relative pt-36 pb-20 min-h-[70vh] flex items-center bg-[#00213a] overflow-hidden"
          >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

            <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center text-white">
              <h1 className="text-4xl font-semibold mb-4">Profile Not Found</h1>
              <p className="text-white/80 mb-8">
                We couldn't find a faculty or staff member matching that link.
              </p>
              <Link
                to="/directory/faculty"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#00213a] font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeftIcon /> Back to Directory
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main id="main-content" className="pt-0">
        {/* Hero */}
        <section
          className="relative pt-36 pb-28 bg-[#00213a] overflow-hidden"
        >
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0"
            style={{ height: '4rem', background: 'linear-gradient(to bottom, rgba(0,33,58,0) 0%, rgba(255,255,255,1) 100%)' }}
            aria-hidden="true"
          />

          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <Link
              to="/directory/faculty"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-8"
            >
              <ArrowLeftIcon /> Faculty &amp; Staff Directory
            </Link>

            <div className="flex flex-col md:flex-row items-center md:items-end gap-8 text-center md:text-left">
              <div className="w-32 h-32 rounded-2xl bg-[#fdbb2e] text-[#00213a] font-black text-5xl flex items-center justify-center shadow-xl flex-shrink-0">
                {getInitials(person.name)}
              </div>
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest">
                    {CATEGORY_LABEL[person.group]}
                  </span>
                </div>
                <h1
                  className="text-4xl lg:text-6xl font-semibold mb-3 tracking-tight"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  {person.name}
                </h1>
                <p className="text-xl text-white/90 font-medium">
                  {person.role}
                  <span className="text-white/60"> · </span>
                  {SCHOOL_INFO.name}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact / School Info */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 -mt-16 mb-16 relative z-10">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-md p-6 lg:p-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center text-[#00213a]">
                  <MailIcon />
                </div>
                Contact
              </h2>
              {person.email ? (
                <>
                  <a
                    href={`mailto:${person.email}`}
                    className="inline-flex items-center gap-2 text-[#00213a] font-semibold hover:underline break-all"
                  >
                    {person.email}
                  </a>
                  <p className="text-sm text-gray-500 mt-3">
                    Click to compose an email. Please allow 24–48 hours for a
                    response during the school week.
                  </p>
                </>
              ) : (
                <p className="text-gray-700">
                  Please contact the main office at {SCHOOL_INFO.phone} to reach {person.name}.
                </p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-md p-6 lg:p-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#fdbb2e]/10 flex items-center justify-center text-[#00213a]">
                  <LocationIcon />
                </div>
                School Information
              </h2>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="font-semibold text-gray-900">{SCHOOL_INFO.name}</li>
                <li>{SCHOOL_INFO.address}</li>
                <li className="flex items-center gap-2">
                  <PhoneIcon />
                  <span>{SCHOOL_INFO.phone}</span>
                </li>
                <li className="text-gray-500">Fax: {SCHOOL_INFO.fax}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Back to directory CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Looking for someone else?
            </h2>
            <p className="text-gray-600 mb-8">
              Browse our complete list of faculty, administrators, and staff.
            </p>
            <Link
              to="/directory/faculty"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00213a] text-white font-semibold rounded-lg hover:bg-[#003a5d] transition-colors"
            >
              <ArrowLeftIcon /> View Full Directory
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FacultyProfilePage;
